import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

/** Inline verification-code input shown when an account is `awaiting_code` —
 *  Instagram asked for a one-time code mid-login and the worker is holding the
 *  browser open, polling the API for what the operator types here. */
export function EnterCodeField({
  accountId,
  personaId,
}: {
  accountId: string;
  personaId: string;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');

  const m = useMutation({
    mutationFn: () => api.submitLoginCode(accountId, code.trim()),
    onSuccess: () => {
      toast.success('Code submitted — finishing login…');
      setCode('');
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'accounts'] });
    },
    onError: (err) => toast.error('Could not submit code', { description: String(err) }),
  });

  return (
    <div className="flex items-center gap-2">
      <Input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="IG code"
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="Instagram verification code"
        className="h-8 w-28"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && code.trim()) m.mutate();
        }}
      />
      <Button size="sm" onClick={() => m.mutate()} disabled={!code.trim() || m.isPending}>
        {m.isPending ? 'Submitting…' : 'Submit'}
      </Button>
    </div>
  );
}
